import {
  Appointment,
  DISCOUNT_TYPE,
  RECEIPT_PAYMENT_MODE_ID,
  TCreateReceiptBody,
  TSelectedSkuItem,
  TYPES_OF_APT,
} from "@elixir/types";
import dateFormat from "dateformat";
import { Patient } from "../apis/tryst/types";
import { QueueType } from "../stores/queue-store/types";
import { QueueConfigItemType } from "../types/appointment";
import { isDoctorOnly } from "./bridges/methods";

export const getReceiptObjectForRequest = ({
  clinicId,
  aid,
  selectedSkus,
  selectedPatient,
}: {
  clinicId: string;
  aid: string;
  selectedSkus: Array<TSelectedSkuItem>;
  selectedPatient: Patient;
}): TCreateReceiptBody => {
  const totalAmount = selectedSkus.reduce(
    (currAmount, sku) => currAmount + sku.price,
    0
  );

  // Construct full phone number with country code: priority is phone.code > ccd > default +91
  let fullPhoneNumber = selectedPatient.mobile || "";
  const countryCode =
    (selectedPatient as any)?.phone?.code ||
    (selectedPatient as any)?.ccd ||
    "+91";
  if (fullPhoneNumber && !fullPhoneNumber.startsWith("+")) {
    fullPhoneNumber = `${countryCode}${fullPhoneNumber}`;
  }

  return {
    meta: {
      clinic_id: clinicId,
      patient_oid: selectedPatient.oid!,
      aid,
    },
    payload: {
      remarks: "",
      amount_due: 0,
      name: `${selectedPatient.fn}`,
      mobile: fullPhoneNumber,
      receipt_amount: totalAmount || 0,
      additional_discount_value: 0,
      additional_discount_type: DISCOUNT_TYPE.AMOUNT,
      additional_discount_amount: 0,
      net_amount: totalAmount || 0,
      receipt_payments: [
        {
          amount: totalAmount || 0,
          paymode: RECEIPT_PAYMENT_MODE_ID.CASH,
        },
      ],
      receipt_skus: selectedSkus.map((sku) => ({
        service_id: sku?.service_id || null,
        service_name: sku.service_name.toLowerCase(),
        quantity: 1,
        price: sku?.price || 0,
        amount: sku?.price || 0,
        discount_type: DISCOUNT_TYPE.AMOUNT,
        discount_value: 0,
        discount_amount: 0,
        net_amount: sku?.price || 0,
      })),
    },
  };
};

export const areTwoTagArraysSame = ({
  tagArr1,
  tagArr2,
}: {
  tagArr1: Array<number>;
  tagArr2: Array<number>;
}) => {
  if (tagArr1.length !== tagArr2.length) {
    return false;
  }
  const sortedTag1 = tagArr1.sort();
  const sortedTag2 = tagArr2.sort();

  let i = sortedTag1.length;
  while (i--) {
    if (sortedTag1[i] !== sortedTag2[i]) {
      return false;
    }
  }
  return true;
};

// const queueObjectTypeMap: Record<QueueType, 'booked' | 'completed' | 'opd' | 'followup' | 'other'> =
//   {
//     booked: 'booked',
//     completed: 'completed',
//     'checked_in': 'opd',
//     'follow-up': 'followup',
//     other: 'other',
//   };

// export const getExpandedCurrentSelectedQueueType = ({ queueType }: { queueType: QueueType }) => {
//   return queueObjectTypeMap[queueType];
// };

export const getPatientLastVisitDetails = ({
  visit_count,
  last_visit_time,
  visit_type = TYPES_OF_APT.REGULAR,
}: Pick<Appointment, "visit_count" | "last_visit_time" | "visit_type">) => {
  const textArr: string[] = [];

  if (visit_type === TYPES_OF_APT.FOLLOW_UP) {
    textArr.push("Follow Up");
  }

  const visitCountText = visit_count ? `Total Visits: ${visit_count}` : null;

  if (visitCountText) {
    textArr.push(visitCountText);
  }

  const lastVisitTimeDateObj = last_visit_time
    ? new Date(last_visit_time)
    : null;

  const lastVisitText =
    visit_type === TYPES_OF_APT.FOLLOW_UP
      ? null
      : lastVisitTimeDateObj
        ? `Last Visit: ${dateFormat(lastVisitTimeDateObj, "dd mmm’yy")}`
        : "New Patient | 0 Visits";

  if (lastVisitText) {
    textArr.push(lastVisitText);
  }

  return textArr.join(" | ");
};

// export const getQueueTypeFirstTimeConfigMapping = ({
//   queueType,
// }: {
//   queueType: QueueType;
// }): keyof FirstTimeConfigType | undefined => {
//   switch (queueType) {
//     case "booked":
//       return "bookedQueueIntroSheetSeen";
//     case "completed":
//       return "completedQueueIntroSheetSeen";
//     case "checked_in":
//       return "myOpdIntroSheetSeen";
//     case "follow-up":
//       return "followupQueueIntroSheetSeen";
//     case "other":
//     default:
//       return undefined;
//   }
// };

export const getActionsItemList = (
  queueType: QueueType
): QueueConfigItemType[] => {
  const isDoctorPersonaOnly = isDoctorOnly();
  return [
    ...(["checkin"].includes(queueType)
      ? [
          {
            columnId: "AVLR",
            title: "Add vitals & lab results",
            subtitle:
              "Add vitals of your patients. These are automatically copied to your Rx as well",
            isSelected: false,
          },
        ]
      : []),
    ...(isDoctorPersonaOnly
      ? [
          {
            columnId: "RMDR",
            title: "Request Medical Records",
            subtitle: "Ask the patient to upload previous health records",
            isSelected: false,
          },
        ]
      : []),
    {
      columnId: "REQP",
      title: "Request Payment",
      subtitle: "Send a payment link to the patient",
      isSelected: false,
    },
    {
      columnId: "AAPT",
      title: "Archive Appointment",
      subtitle: "Archive patient’s visit",
      isSelected: false,
    },
    ...(isDoctorPersonaOnly
      ? [
          {
            columnId: "CRMC",
            title: "Create Medical Certificate",
            subtitle: "Create & send medical certificates to your patients",
            isSelected: false,
          },
        ]
      : []),
    {
      columnId: "RTOD",
      title: "Refer to Doctor",
      subtitle: "Refer patient to another doctor",
      isSelected: false,
    },
    {
      columnId: "SGRL",
      title: "Send Google Review Link",
      subtitle: "Eka will send a review request to patients on your behalf",
      isSelected: false,
    },
    ...(["completed"].includes(queueType)
      ? [
          {
            columnId: "FLWU",
            title: "Follow Up",
            subtitle: "Schedule a follow-up appointment with the patient",
            isSelected: false,
          },
        ]
      : []),
    {
      columnId: "ABHA",
      title: "Create ABHA",
      subtitle:
        "Create patient’s ABHA ID to store all the health records at one place",
      isSelected: false,
    },
    {
      columnId: "UMRR",
      title: "Upload Medical Records",
      subtitle:
        "Upload patient’s previous health records to Eka. This will help you to keep all the records at one place",
      isSelected: false,
    },
    {
      columnId: "MNSH",
      title: "No Show",
      subtitle: "Mark patient as no show",
      isSelected: false,
    },
  ];
};

export const getColumnItemList = (
  queueType: QueueType
): QueueConfigItemType[] => [
  {
    columnId: "TAGS",
    title: "Tags",
    subtitle:
      "Manage & categorise your OPD with tags like “emergency, “priority” etc.  ",
    isSelected: false,
  },
  {
    columnId: "VITY",
    title: "Visit Type",
    subtitle:
      "See patient’s visit type, i.e, walk-in, online booking, follow-up, etc.",
    isSelected: false,
  },
  {
    columnId: "PAYS",
    title: "Billing Status",
    subtitle: "See if the patient has paid for the visit or not.",
    isSelected: false,
  },
  {
    columnId: "ASST",
    title: "Self Assessment Status",
    subtitle: "Request and View Self Assessment by the patient.",
    isSelected: false,
  },
  {
    columnId: "PRES",
    title: "Past Visits",
    subtitle: "See total visits & the last visit date of your patient",
    isSelected: false,
  },
  {
    columnId: "ABHA",
    title: "ABHA ID",
    subtitle: "See if the patient has an ABHA ID. If not, create one for them.",
    isSelected: false,
  },
];

export const getDiffBetween2DateObjects = ({
  date1,
  date2,
}: {
  date1: Date;
  date2: Date;
}): {
  timeDiff: number;
  format: "mins" | "hours";
  timeDiffString: string;
} => {
  const date1Timestamp = date1.getTime();
  const date2Timestamp = date2.getTime();

  const timeStampDiff = Math.abs(date1Timestamp - date2Timestamp);

  const diffInMinutes = Math.round(timeStampDiff / 60000);

  const hours = Math.trunc(diffInMinutes / 60);

  if (hours > 0) {
    return {
      timeDiff: hours,
      format: "hours",
      timeDiffString: `${String(hours).padStart(2, "0")}h`,
    };
  }
  return {
    timeDiff: diffInMinutes,
    format: "mins",
    timeDiffString: `${String(diffInMinutes).padStart(2, "0")}m`,
  };
};

export function getTimeBasedState({
  timeToCheck,
  isOngoing = false,
  currTime = new Date(),
}: {
  timeToCheck: string | Date;
  isOngoing?: boolean;
  currTime?: Date;
}): {
  textColor: string;
  borderColor: string;
  timeDiffString: string;
} {
  const now = currTime;
  const dateTime = new Date(timeToCheck);

  const { format, timeDiffString, timeDiff } = getDiffBetween2DateObjects({
    date1: now,
    date2: dateTime,
  });

  if (format === "hours") {
    return {
      textColor: "dr-text-darwin-touch-yellow-primary",
      borderColor: "dr-border-darwin-touch-yellow-primary",
      timeDiffString,
    };
  } else if (format === "mins") {
    if (timeDiff >= 20) {
      return {
        textColor: "dr-text-darwin-touch-yellow-primary",
        borderColor: "dr-border-darwin-touch-yellow-primary",
        timeDiffString,
      };
    } else if (timeDiff >= 10) {
      return {
        textColor: "dr-text-darwin-touch-yellow-primary",
        borderColor: "dr-border-darwin-touch-yellow-primary",
        timeDiffString,
      };
    } else {
      if (isOngoing) {
        return {
          textColor: "dr-text-darwin-touch-green-dark",
          borderColor: "dr-border-darwin-touch-green-primary",
          timeDiffString,
        };
      }
      return {
        textColor: "dr-text-darwin-touch-neutral-800",
        borderColor: "dr-border-darwin-touch-neutral-100",
        timeDiffString,
      };
    }
  }

  return {
    textColor: "dr-text-darwin-touch-neutral-800",
    borderColor: "dr-border-darwin-touch-neutral-100",
    timeDiffString,
  };
}

export function getAptSlotPrefFromTime(date: Date) {
  const hour = (date as Date).getHours();
  if (hour === 9) {
    return "Morning";
  } else if (hour === 15) {
    return "Afternoon";
  }
  return "Evening";
}
